import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Check, ExternalLink, ExternalLinkIcon, SquarePen, X } from 'lucide-react'
import { useMemo } from 'react'

import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { adminRecordInspector } from 'rails_routes'
import { Badge } from 'slab'
import { useGetAdminEmployersEmployerIdComplianceOwners } from 'utils/web_api'
import { type AdminComplianceOwner } from 'utils/web_api.schemas'

interface EmployerComplianceOwnersTableProps {
  employerId: string
  setIsSheetOpen: (isOpen: boolean) => void
  setSelectedOwner: (owner: AdminComplianceOwner | undefined) => void
}

const columnHelper = createColumnHelper<AdminComplianceOwner>()

const createColumns = (setIsSheetOpen: (isOpen: boolean) => void, setSelectedOwner: (owner: AdminComplianceOwner | undefined) => void) => [
  columnHelper.accessor('id', {
    header: 'ID',
    cell: ({ getValue }) => (
      <Badge
        type='info'
        actionIcon={ExternalLink}
        onActionClick={() => {
          window.open(adminRecordInspector.show({ id: getValue() }), '_blank')
        }}
      >
        {getValue().slice(-6)}
      </Badge>
    ),
  }),
  columnHelper.display({
    id: 'name',
    header: 'Name',
    cell: ({ row }) => {
      const firstName = row.original.first_name || ''
      const lastName = row.original.last_name || ''
      const fullName = [firstName, lastName].filter(Boolean).join(' ')
      return fullName || '-'
    },
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor('is_beneficial_owner', {
    header: 'Beneficial Owner',
    cell: ({ getValue }) => {
      const isBeneficialOwner = getValue()
      return (
        <div className='flex'>
          {isBeneficialOwner
            ? (
              <div className='flex h-6 w-6 items-center justify-center rounded-full bg-green-100'>
                <Check className='h-4 w-4 text-green-600' />
              </div>
              )
            : (
              <div className='flex h-6 w-6 items-center justify-center rounded-full bg-red-100'>
                <X className='h-4 w-4 text-red-600' />
              </div>
              )}
        </div>
      )
    },
  }),
  columnHelper.accessor('is_controlling_officer', {
    header: 'Controlling Officer',
    cell: ({ getValue }) => {
      const isControllingOfficer = getValue()
      return (
        <div className='flex'>
          {isControllingOfficer
            ? (
              <div className='flex h-6 w-6 items-center justify-center rounded-full bg-green-100'>
                <Check className='h-4 w-4 text-green-600' />
              </div>
              )
            : (
              <div className='flex h-6 w-6 items-center justify-center rounded-full bg-red-100'>
                <X className='h-4 w-4 text-red-600' />
              </div>
              )}
        </div>
      )
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const remoteLink = row.original.remote_link

      return (
        <div className='flex item-center justify-start space-x-2 text-base'>
          <DisplayTooltipOnHover text='Edit'>
            <SquarePen
              size={16}
              strokeWidth={2}
              className='cursor-pointer'
              onClick={() => {
                setSelectedOwner(row.original)
                setIsSheetOpen(true)
              }}
            />
          </DisplayTooltipOnHover>
          {remoteLink && (
            <DisplayTooltipOnHover text='Provider link'>
              <ExternalLinkIcon
                size={16}
                strokeWidth={2}
                className='cursor-pointer'
                onClick={() => {
                  window.open(remoteLink, '_blank')
                }}
              />
            </DisplayTooltipOnHover>
          )}
        </div>
      )
    }
  })
]

const EmployerComplianceOwnersTable = ({ employerId, setIsSheetOpen, setSelectedOwner }: EmployerComplianceOwnersTableProps) => {
  const { data: complianceOwners, isLoading, error } = useGetAdminEmployersEmployerIdComplianceOwners(
    employerId,
    { query: { enabled: !!employerId } }
  )

  // Memoize compliance owners data to prevent infinite re-renders
  const owners = useMemo(() => {
    return (complianceOwners as AdminComplianceOwner[]) || []
  }, [complianceOwners])

  const columns = useMemo(() => createColumns(setIsSheetOpen, setSelectedOwner), [])

  const table = useReactTable({
    data: owners,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={!!error}
      noDataMessage='No owners found for this employer'
      tableMessageHeightClass='h-14'
      paginationLabelSingular='owner'
      paginationLabelPlural='owners'
      loadingSkeletonPageSizeOverride={5}
    />
  )
}

export default EmployerComplianceOwnersTable
